import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import Navigation from "@/components/Navigation";
import { 
  ArrowLeft, 
  CheckCircle2, 
  BookOpen, 
  FileText,
  Lightbulb,
  ClipboardCheck,
  Trophy
} from "lucide-react";

const ModuleView = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(-1);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    fetchModuleData();
  }, [id]);

  // Auto-scroll when section is expanded
  useEffect(() => {
    if (expandedSection >= 0) {
      setTimeout(() => {
        const element = document.querySelector(`[data-testid="section-${expandedSection}-card"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [expandedSection]);

  const fetchModuleData = async () => {
    try {
      const [moduleRes, progressRes] = await Promise.all([
        axios.get(`${API}/modules/${id}`),
        axios.get(`${API}/progress`)
      ]);
      setModule(moduleRes.data);
      const moduleProgress = progressRes.data.find(p => p.module_id === id);
      setProgress(moduleProgress || { completed_sections: [], quiz_attempts: [], best_score: 0 });
    } catch (error) {
      toast.error("Erreur lors du chargement du module");
    } finally {
      setLoading(false);
    }
  };

  const markSectionComplete = async (sectionIndex) => {
    try {
      await axios.post(`${API}/progress/${id}/section/${sectionIndex}`);
      setProgress(prev => ({
        ...prev,
        completed_sections: [...new Set([...prev.completed_sections, sectionIndex])]
      }));
      
      // Show celebration animation
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
      
      toast.success("🎉 Section complétée!");
      
      // Auto-navigate to next section if exists
      if (sectionIndex < module.sections.length - 1) {
        setTimeout(() => {
          setExpandedSection(sectionIndex + 1);
        }, 1500);
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const goToPreviousSection = () => {
    if (expandedSection > 0) {
      setExpandedSection(expandedSection - 1);
    }
  };

  const goToNextSection = () => {
    if (expandedSection < module.sections.length - 1) {
      setExpandedSection(expandedSection + 1);
    } else if (allSectionsRead) {
      // Si dernière section et tout est lu, aller au quiz
      navigate(`/module/${id}/quiz`);
    }
  };

  const estimateReadingTime = (content) => {
    const words = content.split(/\s+/).length;
    const minutesMin = Math.ceil(words / 200);
    const minutesMax = Math.ceil(words / 150);
    return `${minutesMin}-${minutesMax} min`;
  };

  const isSectionCompleted = (index) => {
    return progress?.completed_sections?.includes(index) || false;
  };

  if (loading || !module) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const completedSections = progress?.completed_sections?.length || 0;
  const totalSections = module.sections.length;
  const sectionProgress = totalSections > 0 ? (completedSections / totalSections) * 100 : 0;
  const allSectionsRead = completedSections === totalSections;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-cream-50">
      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="animate-bounce">
            <div className="text-8xl">🎉</div>
          </div>
        </div>
      )}

      <Navigation user={user} onLogout={onLogout} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Module Header */}
        <div className="mb-6 md:mb-8 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-orange-100 to-purple-100 rounded-xl">
                <BookOpen className="w-6 h-6 md:w-7 md:h-7 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Module {module.order}: {module.title}
                </h1>
                <p className="text-gray-600 text-sm md:text-base mt-1">{module.description}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {progress?.completed && (
                <Badge className="bg-green-500 text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Complété
                </Badge>
              )}
              {progress?.best_score > 0 && (
                <Badge variant="outline" className="border-orange-500 text-orange-600">
                  Score: {progress.best_score.toFixed(0)}%
                </Badge>
              )}
            </div>
          </div>
          
          <Card className="border-0 shadow-md mt-4">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="space-y-2 flex-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">
                      📚 {completedSections} sur {totalSections} sections complétées
                    </span>
                    <span className="font-bold text-orange-600">{sectionProgress.toFixed(0)}%</span>
                  </div>
                  <Progress value={sectionProgress} className="h-3" data-testid="section-progress-bar" />
                </div>
              </div>
              {allSectionsRead && !progress?.completed && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 animate-pulse">
                  <p className="text-sm text-green-800 font-medium text-center">
                    ✅ Toutes les sections lues! Vous pouvez passer au quiz maintenant.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sections */}
        <div className="space-y-4 mb-8">
          {module.sections.map((section, index) => {
            const isCompleted = isSectionCompleted(index);
            const isExpanded = expandedSection === index;

            return (
              <Card
                key={index}
                className={`card-hover cursor-pointer border-0 shadow-xl animate-fadeIn hover:shadow-2xl transition-all duration-300 ${ 
                  isCompleted ? "border-l-4 border-l-green-500 bg-gradient-to-br from-green-50/50 to-white" : 
                  !isSectionCompleted(index) && index === 0 ? "border-l-4 border-l-orange-400 bg-gradient-to-br from-orange-50/30 to-white" :
                  "bg-white"
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => setExpandedSection(isExpanded ? -1 : index)}
                data-testid={`section-${index}-card`}
              >
                <CardHeader className="py-6 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-4 rounded-xl shadow-md ${ 
                        isCompleted ? "bg-gradient-to-br from-green-100 to-emerald-100" : "bg-gradient-to-br from-orange-100 to-purple-100"
                      }`}>
                        {section.type === "introduction" && <Lightbulb className="w-7 h-7 text-orange-600" />}
                        {section.type === "content" && <FileText className="w-7 h-7 text-orange-600" />}
                        {section.type === "case_study" && <ClipboardCheck className="w-7 h-7 text-orange-600" />}
                        {section.type === "summary" && <CheckCircle2 className="w-7 h-7 text-green-600" />}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl font-bold">{section.title}</CardTitle>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">📖 {estimateReadingTime(section.content)}</span>
                          {!isCompleted && index === 0 && completedSections === 0 && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">Commencer ici</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {isCompleted ? (
                      <CheckCircle2 className="w-7 h-7 text-green-500" data-testid={`section-${index}-completed-icon`} />
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-gray-300"></div>
                    )}
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="space-y-6 animate-fadeIn px-8 py-8 bg-gradient-to-br from-white to-orange-50/30">
                    <Separator className="my-6" />
                    <div className="space-y-6 max-w-none">
                      {section.content.split('\n\n').map((block, pIndex) => {
                        // Skip empty blocks
                        if (!block.trim()) return null;
                        
                        // Handle main section titles with **Title:**
                        if (block.match(/^\*\*[^*]+:\*\*/)) {
                          const titleMatch = block.match(/^\*\*([^*]+):\*\*/);
                          const title = titleMatch ? titleMatch[1] : '';
                          const content = block.replace(/^\*\*[^*]+:\*\*\s*/, '').trim();
                          
                          return (
                            <div key={pIndex} className="my-8">
                              <div className="flex items-center gap-3 mb-4">
                                <div className="w-2 h-8 bg-gradient-to-b from-orange-500 to-purple-600 rounded-full"></div>
                                <h3 className="text-xl font-bold text-purple-900">{title}</h3>
                              </div>
                              {content && (
                                <div className="ml-5 pl-4 border-l-2 border-gray-200">
                                  <p className="text-gray-700 text-lg leading-relaxed">{content}</p>
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // Handle numbered sub-sections **1. Title**
                        if (block.match(/^\*\*\d+\./)) {
                          const lines = block.split('\n');
                          const titleLine = lines[0];
                          const contentLines = lines.slice(1).join('\n');
                          const title = titleLine.replace(/^\*\*/, '').replace(/\*\*$/, '').trim();
                          
                          return (
                            <div key={pIndex} className="ml-4 my-6 p-5 bg-white rounded-xl border-l-4 border-orange-400 shadow-sm">
                              <h4 className="text-lg font-bold text-orange-700 mb-3">{title}</h4>
                              {contentLines && <p className="text-gray-700 text-base leading-relaxed">{contentLines}</p>}
                            </div>
                          );
                        }
                        
                        // Handle bullet points and checkmarks
                        if (block.match(/^[-✓❌✅]/m)) {
                          const items = block.split('\n').filter(line => line.trim());
                          return (
                            <ul key={pIndex} className="space-y-4 my-6 ml-4">
                              {items.map((item, iIndex) => {
                                const iconMatch = item.match(/^([-✓❌✅])\s*/);
                                const icon = iconMatch ? iconMatch[1] : '-';
                                const text = item.replace(/^[-✓❌✅]\s*/, '').replace(/^\*\*/, '').replace(/\*\*:/, ':');
                                
                                let iconElement;
                                if (icon === '✓' || icon === '✅') {
                                  iconElement = <span className="text-2xl text-green-600">✓</span>;
                                } else if (icon === '❌') {
                                  iconElement = <span className="text-2xl text-red-600">✗</span>;
                                } else {
                                  iconElement = <span className="w-3 h-3 rounded-full bg-gradient-to-br from-orange-500 to-purple-500 mt-2"></span>;
                                }
                                
                                return (
                                  <li key={iIndex} className="flex items-start gap-4 p-3 rounded-lg hover:bg-orange-50/50 transition-colors">
                                    <span className="flex-shrink-0">{iconElement}</span>
                                    <span className="flex-1 text-gray-800 text-base leading-relaxed">{text}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          );
                        }
                        
                        // Regular paragraphs with better spacing
                        return (
                          <p key={pIndex} className="text-gray-700 text-lg leading-relaxed my-5">
                            {block}
                          </p>
                        );
                      })}
                    </div>
                    {section.case_study && (
                      <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl border-2 border-purple-200 shadow-md my-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Lightbulb className="w-5 h-5 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-purple-900 text-lg">Étude de cas</h4>
                        </div>
                        <div className="bg-white p-4 rounded-lg mb-4 border border-purple-100">
                          <p className="text-gray-800 leading-relaxed font-medium">{section.case_study.scenario}</p>
                        </div>
                        <div className="space-y-3">
                          {section.case_study.options.map((option, optIndex) => {
                            const isCorrect = optIndex === section.case_study.correct_option;
                            return (
                              <div
                                key={optIndex}
                                className={`p-4 rounded-lg border-2 transition-all ${ 
                                  isCorrect
                                    ? "bg-green-50 border-green-400 shadow-sm"
                                    : "bg-white border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {isCorrect && (
                                    <div className="p-1 bg-green-100 rounded-full mt-0.5">
                                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    </div>
                                  )}
                                  <p className="text-gray-700 leading-relaxed flex-1">
                                    <span className="font-semibold text-gray-900">{String.fromCharCode(65 + optIndex)}.</span> {option}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-5 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            <strong className="text-blue-900 flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              Explication:
                            </strong>
                            {section.case_study.explanation}
                          </p>
                        </div>
                      </div>
                    )}
                    {!isCompleted && (
                      <Button
                        onClick={() => markSectionComplete(index)}
                        className="w-full btn-primary text-white py-6 text-base"
                        data-testid={`mark-section-${index}-complete-button`}
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Marquer comme lu
                      </Button>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Quiz Button - Floating when all sections read */}
        {allSectionsRead && (
          <div className="fixed bottom-8 right-8 z-40 animate-bounce">
            <Button
              onClick={() => navigate(`/module/${id}/quiz`)}
              className="btn-secondary text-white shadow-2xl px-8 py-6 text-lg"
              data-testid="floating-quiz-button"
            >
              <Trophy className="w-6 h-6 mr-2" />
              Passer au quiz maintenant!
            </Button>
          </div>
        )}

        {/* Quiz Button - Regular */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-50 to-purple-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Évaluation du module
                </h3>
                <p className="text-sm text-gray-600">
                  {allSectionsRead 
                    ? "✅ Toutes les sections lues! Vous êtes prêt pour le quiz."
                    : `📚 Lisez toutes les sections avant de passer le quiz (${completedSections}/${totalSections})`}
                </p>
              </div>
              <Button
                onClick={() => navigate(`/module/${id}/quiz`)}
                className="btn-secondary text-white"
                data-testid="start-quiz-button"
              >
                Commencer le quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ModuleView;
